/**
 * What the sticky "Add to cart" should show and do, given the native main-product button.
 * @param {{disabled: boolean, busy: boolean, text: string, addText: string}} native - Native [name="add"] state.
 * @param {boolean} needsSelection - An option (e.g. size) is still unchosen.
 * @returns {{label: string, disabled: boolean, action: 'add'|'select'|'none'}}
 */
function stickyAtcState(native, needsSelection) {
  // Preselection: the native button reads "Make a selection" and is disabled; the sticky still offers Add to cart and points at the picker.
  if (needsSelection) return { label: native.addText, disabled: false, action: 'select' };
  if (native.disabled) return { label: native.text, disabled: true, action: 'none' };
  return { label: native.text || native.addText, disabled: false, action: native.busy ? 'none' : 'add' };
}

/**
 * Hide the sticky bar while the buy box is usable. Mobile: hidden until the form has scrolled above the viewport.
 * Desktop: hidden while any of the product info is on screen.
 */
function stickyAtcOut(rect, viewportHeight, desktop) {
  return desktop ? rect.bottom > 0 && rect.top < viewportHeight : rect.bottom > 0;
}

if (typeof module !== 'undefined') module.exports = { stickyAtcState, stickyAtcOut };

if (typeof customElements !== 'undefined' && !customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');
      this.addBtn = this.querySelector('.js-sticky-add');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      window.removeEventListener('scroll', this.boundSchedule);
      window.removeEventListener('resize', this.boundSchedule);
      cancelAnimationFrame(this.frame);
      if (this.observer) this.observer.disconnect();
      if (this.formObserver) this.formObserver.disconnect();
      if (this.productForm) this.productForm.removeEventListener('on:cart:error', this.boundShowError);
    }

    bindEvents() {
      // IntersectionObserver alone misses a direct jump from "form below the viewport" to "form above it" (both
      // non-intersecting), so scroll/resize also schedule one geometry check per animation frame.
      this.boundSchedule = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.updateVisibility());
      };
      this.observer = new IntersectionObserver(this.boundSchedule);
      if (this.productForm) this.observer.observe(this.productForm);
      if (this.productInfo) this.observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
      window.addEventListener('scroll', this.boundSchedule, { passive: true });
      window.addEventListener('resize', this.boundSchedule, { passive: true });
      this.boundSchedule();

      if (!this.addBtn || !this.productForm) return;
      // Mirror the native button (label, sold out/unavailable, loading) whenever the theme changes it.
      this.formObserver = new MutationObserver(() => this.sync());
      this.formObserver.observe(this.productForm, {
        subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
      this.addBtn.addEventListener('click', this.handleClick.bind(this));
      // Native add errors render in the form's alert, which is off-screen while the sticky bar is in use.
      this.boundShowError = () => {
        if (!this.submitting) return;
        this.submitting = false;
        const error = this.productForm.querySelector('.js-form-error');
        if (error && !error.hidden) error.scrollIntoView({ block: 'center' });
      };
      this.productForm.addEventListener('on:cart:error', this.boundShowError);
      this.sync();
    }

    updateVisibility() {
      const target = theme.mediaMatches.md ? this.productInfo : this.productForm;
      if (!target) return;
      this.classList.toggle('sticky-atc-panel--out', stickyAtcOut(target.getBoundingClientRect(), window.innerHeight, theme.mediaMatches.md));
    }

    nativeBtn() {
      return this.productForm.querySelector('[name="add"]');
    }

    needsSelection() {
      const picker = this.productSection.querySelector('variant-picker');
      return Boolean(picker && (picker.preSelection || picker.classList.contains('variant-picker--preselection')));
    }

    state() {
      const btn = this.nativeBtn();
      if (!btn) return null;
      return stickyAtcState({
        disabled: btn.disabled,
        busy: btn.getAttribute('aria-disabled') === 'true',
        text: btn.textContent.trim(),
        addText: btn.dataset.addToCartText
      }, this.needsSelection());
    }

    sync() {
      const state = this.state();
      if (!state) return;
      const btn = this.nativeBtn();
      this.addBtn.textContent = state.label;
      this.addBtn.disabled = state.disabled;
      this.addBtn.classList.toggle('is-loading', btn.classList.contains('is-loading'));
      this.addBtn.classList.toggle('is-success', btn.classList.contains('is-success'));
      if (this.prompt && state.action !== 'select') {
        this.prompt.hidden = true;
        this.prompt = null;
      }
    }

    handleClick() {
      const state = this.state();
      if (!state || state.action === 'none') return;
      if (state.action === 'add') {
        this.submitting = true;
        this.nativeBtn().click(); // Native submit: same validation, product-form add, drawer and errors.
        return;
      }
      this.promptSelection();
    }

    /**
     * Scrolls to the native picker, focuses the first unchosen option and says what is missing. Never picks one.
     */
    promptSelection() {
      const picker = this.productSection.querySelector('variant-picker');
      const chosen = (el) => el.querySelector('input:checked, .custom-select__option[aria-selected="true"]:not([data-value=""])');
      const missing = [...picker.querySelectorAll('.option-selector')].find((el) => !chosen(el));
      const anchor = this.productSection.querySelector(`#${this.addBtn.dataset.anchor}`) || picker;
      anchor.scrollIntoView();
      const scope = missing || picker;
      const target = scope.querySelector('input:not(:disabled)') || scope.querySelector('.custom-select__btn, button');
      if (target) target.focus({ preventScroll: true });

      const error = this.productForm.querySelector('.js-form-error');
      if (!error) return;
      const name = missing && missing.querySelector('legend, .label');
      error.hidden = false; // Visible before the text changes, so role="alert" announces it.
      error.textContent = name ? `${theme.strings.noSelectedVariant}: ${name.firstChild.textContent.trim()}` : theme.strings.noSelectedVariant;
      this.prompt = error;
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
